/**
 * Emit hosts for the `document` analytics group — 17 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';
import { getEmailDomain } from '@/utils/emailDomains';
import { getCurrentSessionId } from '@heidi/runtime/shell';

// Document
export function documentCreateClick() {
  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.DocumentCreateClick,
    },
  });
}

export function documentCreatedByAICommand(aiCommandId?: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.DocumentCreatedByAICommand,
      aiCommandId: aiCommandId,
    },
  });
}

export function documentCreatedByAskAI(aiCommandText?: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.DocumentCreatedByAskAI,
      aiCommandText: aiCommandText,
    },
  });
}

export function documentCreatedByTemplate(templateId?: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.DocumentCreatedByTemplate,
      templateId: templateId,
    },
  });
}

export function documentDeleted(input: {
  is_pinned_template?: boolean;
  is_starter_pack_template?: boolean;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.DocumentDeleted,
      ...(input.is_pinned_template !== undefined && {
        is_pinned_template: input.is_pinned_template,
      }),
      ...(input.is_starter_pack_template !== undefined && {
        is_starter_pack_template: input.is_starter_pack_template,
      }),
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.DocumentDeleted,
    props: {
      is_pinned_template: input.is_pinned_template,
      is_starter_pack_template: input.is_starter_pack_template,
    },
  });
}

export function documentRedo() {
  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.DocumentReDo,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.DocumentUpdated,
    props: {
      update_action: 'redo',
    },
  });
}

export function documentRegeneratedByAskAI(aiCommandId: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.DocumentRegeneratedByAskAI,
      aiCommandId,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.DocumentUpdated,
    props: {
      update_action: 'regenerated_by_ask_ai',
      ai_command_id: aiCommandId,
    },
  });
}

export function documentRegeneratedByTemplate(templateId: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.DocumentRegeneratedByTemplate,
      templateId,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.DocumentUpdated,
    props: {
      update_action: 'regenerated_by_template',
      template_id: templateId,
    },
  });
}

export function documentSelectionCopyAll() {
  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.DocumentSelectionCopyAll,
    },
  });

  // V2
  const sessionId = getCurrentSessionId();
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.DocumentCopied,
    props: {
      ...(sessionId != null && { session_id: sessionId }),
    },
  });
}

export function documentSelectionRegenerateOutput() {
  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.DocumentSelectionRegenerateOutput,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.DocumentUpdated,
    props: {
      update_action: 'regenerate_output',
    },
  });
}

export function documentSelectionSaveAsNew() {
  // Intent signal only — the authoritative `template_created` fires at save time.
  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.DocumentSelectionSaveAsNew,
    },
  });
}

export function documentSelectionSendEmail({
  email,
  documentId,
  sessionId,
  is_pinned_template,
  is_starter_pack_template,
  recipient_count,
  included_link,
  link_scope,
  link_ttl,
  partial_failure,
}: {
  email: string;
  documentId: string;
  /** Session the document belongs to. Omitted when not in a session context. */
  sessionId?: string;
  is_pinned_template: boolean;
  is_starter_pack_template: boolean;
  /** Total number of recipients targeted by the send. */
  recipient_count?: number;
  /** True when the merged modal's "Read-only session link" was on at submit. */
  included_link?: boolean;
  /** Scope of the link share. Only set when included_link is true. */
  link_scope?: string;
  /** TTL of the link share. Only set when included_link is true. */
  link_ttl?: string;
  /** True when at least one of the two mutations failed but at least one succeeded. */
  partial_failure?: boolean;
}) {
  // Sanitise recipient PII: only the email domain leaves the client, never
  // the full address (mirrors the `email_domain` user-trait pattern above).
  const sentDocumentEmailDomain = getEmailDomain(email);

  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.DocumentSelectionSendEmail,
      sentDocumentEmail: sentDocumentEmailDomain,
      documentId: documentId,
      is_pinned_template,
      is_starter_pack_template,
      ...(recipient_count !== undefined ? { recipient_count } : {}),
      ...(included_link !== undefined ? { included_link } : {}),
      ...(included_link && link_scope ? { link_scope } : {}),
      ...(included_link && link_ttl ? { link_ttl } : {}),
      ...(partial_failure !== undefined ? { partial_failure } : {}),
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.DocumentShared,
    props: {
      document_id: documentId,
      ...(sessionId ? { session_id: sessionId } : {}),
      is_pinned_template,
      is_starter_pack_template,
      ...(recipient_count !== undefined ? { recipient_count } : {}),
      ...(included_link !== undefined ? { included_link } : {}),
      ...(included_link && link_scope ? { link_scope } : {}),
      ...(included_link && link_ttl ? { link_ttl } : {}),
      ...(partial_failure !== undefined ? { partial_failure } : {}),
    },
  });
}

export function documentSelectionSetAsDefaultTemplate() {
  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.DocumentSelectSetAsDefaultTemplate,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateUpdated,
    props: {
      template_editor_version: 'v1',
    },
  });
}

export function documentSelectionViewStructure() {
  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.DocumentSelectionViewStructure,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.DocumentViewed,
    props: {
      view_action: 'view_structure',
    },
  });
}

export function documentSyncChangesClicked() {
  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.DocumentSyncChangesClicked,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.DocumentSynced,
    props: {},
  });
}

export function documentUndo() {
  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.DocumentUndo,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.DocumentUpdated,
    props: {
      update_action: 'undo',
    },
  });
}

// Note
export function noteSelectionRegenerateOutput() {
  trackLegacyEvent({
    name: SegmentObjectName.Document,
    props: {
      action: SegmentActionName.NoteSelectionRegenerateOutput,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.DocumentUpdated,
    props: {
      update_action: 'regenerate_output',
    },
  });
}
